import { Component, Input, inject } from '@angular/core';
import { isString } from '../../data-model';
import { ValidationService } from '../validation.service';
import { ParsedBestand } from './bestand-model';
import { applyBestand, istParsedSeaKonform, parseBestand, revertBestand } from './bestand-parser';

/**
 * Wiederverwendbarer Bestand-Konverter (Textfeld + SEA-Umschrift + Rückgängig).
 * Wird sowohl im eigenen Bestand-Bereich als auch im Vegetationsreiter
 * eingebunden — beide spiegeln dasselbe Feld `extendedHeader.baumarten`.
 */
@Component({
	standalone: false,
	selector: 'bp-bestand-converter',
	templateUrl: './bestand-converter.component.html',
	styleUrls: ['./bestand-converter.component.css'],
})
export class BestandConverterComponent {
	// `any` wie bei den hl-area-Komponenten (sonst scheitert das strikte Template
	// am `Baumart[] | string`-Feld beim Zwei-Wege-Binding).
	@Input() data: any;

	private vs = inject(ValidationService);
	private cacheText?: string;
	private cacheResult?: ParsedBestand;

	/** true, wenn ein konvertierbarer Bestand-Text vorliegt (kein Probestämme-Array). */
	istText(): boolean {
		return isString(this.data?.extendedHeader?.baumarten);
	}

	private rohText(): string {
		const b = this.data?.extendedHeader?.baumarten;
		return isString(b) ? b : '';
	}

	/** Konvertierungs-Vorschlag (gecacht pro Text). */
	vorschlag(): ParsedBestand {
		const t = this.rohText();
		if (t !== this.cacheText || !this.cacheResult) {
			this.cacheText = t;
			this.cacheResult = parseBestand(t);
		}
		return this.cacheResult;
	}

	/** Text ist bereits identisch mit der kanonischen SEA-Form. */
	istKonform(): boolean {
		return this.vorschlag().kanonisch === this.rohText().trim();
	}

	/** Text erfüllt die volle SEA-Konformität (keine offenen Hinweise, eindeutig,
	 *  unveränderlich beim Konvertieren). Gleiche Regel wie der „Bestand"-Validator. */
	istSeaKonform(): boolean {
		return istParsedSeaKonform(this.vorschlag());
	}

	/** Der aktuelle Bestand-Text wurde bewusst als (abweichende) Beschreibung
	 *  bestätigt — passt exakt zum gespeicherten Bestätigungstext. */
	istBestaetigt(): boolean {
		const b = this.data?.extendedHeader?.bestandBestaetigt;
		const t = this.original();
		return t !== '' && b != null && String(b).trim() === t;
	}

	/** Bestätigte, weiterhin abweichende Beschreibung → grünes „bestätigt"-Banner. */
	zeigeBestaetigt(): boolean {
		return this.istBestaetigt() && !this.istSeaKonform();
	}

	/** Das Übernehmen würde den Text tatsächlich verändern. */
	geaendert(): boolean {
		return !this.istKonform();
	}

	/** Aktueller Bestand-Text (getrimmt). */
	original(): string {
		return this.rohText().trim();
	}

	/** Offenland/Biotop statt Baumbestand (z.B. „Wiese"). */
	istOffenland(): boolean {
		return this.vorschlag().kind === 'offenland';
	}

	/** Konkrete Hinweise, die der Nutzer klären muss (leer = nichts zu tun). */
	hinweise(): string[] {
		return this.vorschlag().issues;
	}

	/** Der gelbe „Bitte noch klären"-Kasten. Eine bewusst bestätigte Abweichung ist
	 *  geklärt — dann bleiben die Hinweise weg. */
	zeigeHinweise(): boolean {
		return this.hinweise().length > 0 && !this.zeigeBestaetigt();
	}

	/** Es gibt einen gespeicherten Originaltext, der wiederhergestellt werden kann. */
	kannRueckgaengig(): boolean {
		const orig = this.data?.extendedHeader?.bestand?.original;
		return orig != null && orig !== this.original();
	}

	/** Der gespeicherte Originaltext (vor der Konvertierung). */
	gespeichertesOriginal(): string {
		return this.data?.extendedHeader?.bestand?.original ?? '';
	}

	/** Übernimmt den kanonischen SEA-Text + die strukturierte Beschreibung. */
	uebernehmen(): void {
		applyBestand(this.data.extendedHeader, this.vorschlag());
		// Der Text ist jetzt SEA-konform; eine alte Bestätigung des abweichenden
		// Texts wäre veraltet und würde nur verwirren.
		delete this.data.extendedHeader.bestandBestaetigt;
		this.cacheText = undefined;
		this.vs.dirty.next(true);
		this.vs.revalidate.next('Bestand');
	}

	/** Bestätigt die (abweichende) Beschreibung so wie sie ist → Validierung „ok".
	 *  Speichert exakt den aktuellen Text; ein späteres Ändern hebt die Bestätigung auf. */
	bestaetigen(): void {
		this.data.extendedHeader.bestandBestaetigt = this.original();
		this.vs.dirty.next(true);
		// Das Bestand-Feld neu bewerten: nur `bestandBestaetigt` hat sich geändert,
		// der Textwert selbst nicht — sonst bliebe die gelbe Markierung stehen.
		this.vs.revalidate.next('Bestand');
	}

	/** Nimmt die Bestätigung zurück → Bestand wird wieder als abweichend markiert. */
	bestaetigungAufheben(): void {
		delete this.data.extendedHeader.bestandBestaetigt;
		this.vs.dirty.next(true);
		this.vs.revalidate.next('Bestand');
	}

	/** Holt den Originaltext zurück und verwirft die Konvertierung. */
	rueckgaengig(): void {
		if (revertBestand(this.data.extendedHeader)) {
			this.cacheText = undefined;
			this.vs.dirty.next(true);
		}
	}
}
